import React from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { RiskRemediatorMain } from 'src/components/fintech-ops/risk-remediator/RiskRemediator';
import { UpgradeMedicMain } from 'src/components/fintech-ops/upgrade-medic/UpgradeMedicPage';
import { useAuth } from './context/AuthContextProvider';
import { eLDAPGroup } from '../constants/AppConstants';
import { DasFinSuiteApplicationBanner } from './AppBanner';
import { AdminHomePage } from './das-finsuite/admin-components/AdminHomePage';
import { AdminAnnouncements } from './das-finsuite/admin-components/admin-announcements/AdminAnnouncements';
import ManageAnnouncements from './das-finsuite/admin-components/admin-announcements/AdminManageAnnouncements';
import { Applications } from './das-finsuite/admin-components/applications/Applications';
import { AdminNewApp } from './das-finsuite/admin-components/applications/admin-manage-application/AdminNewApp';
import { Feedbacks } from './das-finsuite/admin-components/feedback/Feedbacks';
import AllApps from './das-finsuite/all-apps/AllApps';
import Announcements from './das-finsuite/announcements/Announcements';
import { EmbedAppPage } from './das-finsuite/embedded-apps/EmbedAppPage';
import Tutorials from './das-finsuite/tutorials/Tutorials';

import { DFPHomePage } from './dfp-home-page/DFPHomePage';
import { CostMetricsMainPage } from './fintech-ops/cost-metrics/CostMetricsMainPage';
import { DogmaMain } from './fintech-ops/dogma/DogmaPage';
import { FinTechOpsPage } from './fintech-ops/fintech-ops-dashboard/FinTechOpsDashboard';
import { MappingMainPage } from './fintech-ops/ops-mappings/MappingMainPage';
import { PolicyEnginePage } from './fintech-ops/policy-engine/PolicyEnginePage';
import { SasPageMain } from './fintech-ops/sas/SasPage';
import { TicketsPageMain } from './fintech-ops/tickets/TicketsPage';
import { UserMetricsPage } from './fintech-ops/user-metrics/UserMetricsPage';
import { KpiMetricsPage } from './fintech-ops/kpi-metrics/KpiMetricsPage';
import { HomepageManagement } from './das-finsuite/admin-components/homepage-manage/HomePageManagement';
import { TeamPageManagement } from './das-finsuite/admin-components/teams-manage/TeamPageManagement';
import { FinTechTeamHomePage } from './das-finsuite/fintech-teams/FinTechTeamHomePage';
import { BusinessReportingPage } from './fintech-ops/business-reporting/BusinessReportingPage';
import { EAHuddleHomePage as EAHuddleV2 } from './das-finsuite/ea-huddle-v2/EAHuddleHomePage';
import { ENVIRONMENT_VARIABLES } from '../constants/EnvironmentConstants';
import { DFPTopNavigation } from './dfp-home-page/top-navigation/DFPTopNavigation';
import { ViewMetadata } from './das-finsuite/admin-components/manage-metadata/ViewMetadata';
import { BaseballCardsHomePage } from './das-finsuite/baseball-cards/BaseballCardsHomePage';
import BaseballCardsAdminPage from './das-finsuite/baseball-cards/BaseballCardsAdminPage';
import { AskQuickProvider } from './ask-quick/AskQuickContext';
import { AskQuickPanel } from './ask-quick/AskQuickPanel';
import { ImpersonationProvider } from './context/ImpersonationContext';

export const DasFinSuiteAdminWrapper = () => {
  const userAuthenticationDetails = useAuth();
  if (userAuthenticationDetails.isAdmin) {
    return <Outlet />;
  }
  return <Navigate to="/" replace />;
};

export const FinTechOpsOEReadOnlyWrapper = () => {
  // Previously gated by isOEReadOnly - now open to all users
  // const userAuthenticationDetails = useAuth();
  // if (!userAuthenticationDetails.isOEReadOnly) {
  //   return <Navigate to="/" replace />;
  // }
  return <Outlet />;
};

export const BaseballCardsWrapper = () => {
  const userAuthenticationDetails = useAuth();
  const hasAccess = [eLDAPGroup.BASEBALL_CARDS_LDAP, eLDAPGroup.DEV_LDAP].some((group) => userAuthenticationDetails.userLDAPGroups?.includes(group));
  if (!hasAccess) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
};

export const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <ImpersonationProvider>
        <AskQuickProvider>
          <AskQuickPanel
            header={
              <div id="h">
                <DFPTopNavigation />
                <DasFinSuiteApplicationBanner />
              </div>
            }
          >
            <Routes>
              {/* LandingPage is accessible to all types of users */}
              <Route path="/" element={<DFPHomePage />} />

              {/* FinSuite Pages */}
              <Route path="/all-apps" element={<AllApps />} />
              <Route path="/all-apps/:id" element={<EmbedAppPage />} />
              {/* <Route path="/teams" element={<FinTechTeamHomePage />} /> */}
              <Route path="/announcements" element={<Announcements />} />
              {/* <Route path="/tutorials" element={<Tutorials />} /> */}
              <Route path="/ea-huddle" element={<EAHuddleV2 />} />

              {/* Baseball Cards - accessible to BASEBALL_CARDS_LDAP and DEV_LDAP only */}
              <Route path="baseball-cards" element={<BaseballCardsWrapper />}>
                <Route index element={<BaseballCardsHomePage />} />
              </Route>

              {/* FinTech Ops - accessible to OE read only */}
              <Route path="operational-excellence" element={<FinTechOpsOEReadOnlyWrapper />}>
                <Route index element={<FinTechOpsPage />} />
                <Route path="oe-insights" element={<BusinessReportingPage />} />
                <Route path="ticket-health" element={<TicketsPageMain />} />
                <Route path="user-metrics" element={<UserMetricsPage />} />
                <Route path="security-risks" element={<SasPageMain />} />
                <Route path="policy-engine" element={<PolicyEnginePage />} />
                <Route path="pipeline-health" element={<DogmaMain />} />
                <Route path="risk-remediator" element={<RiskRemediatorMain />} />
                <Route path="upgrade-medic" element={<UpgradeMedicMain />} />
                <Route path="cost-metrics" element={<CostMetricsMainPage />} />
                <Route path="data-mapping" element={<MappingMainPage />} />
                <Route path="kpi-metrics" element={<KpiMetricsPage />} />
              </Route>

              {/* Admin is accessible to admins only */}
              <Route path="admin" element={<DasFinSuiteAdminWrapper />}>
                <Route index element={<AdminHomePage />} />
                <Route path="/admin/applications" element={<Applications />} />
                <Route path="/admin/manage-applications" element={<AdminNewApp />} />
                <Route path="/admin/manage-applications/:id" element={<AdminNewApp />} />
                <Route path="/admin/announcements" element={<AdminAnnouncements />}>
                  <Route path="/admin/announcements/:id" element={<ManageAnnouncements />} />
                </Route>
                <Route path="/admin/feedbacks" element={<Feedbacks />}></Route>
                <Route path="/admin/manage-homepage" element={<HomepageManagement />}></Route>
                <Route path="/admin/manage-team-page" element={<TeamPageManagement />}></Route>

                <Route path="/admin/view-metadata" element={<ViewMetadata />}></Route>
                <Route path="/admin/baseball-cards" element={<BaseballCardsAdminPage />} />
              </Route>
            </Routes>
          </AskQuickPanel>
        </AskQuickProvider>
      </ImpersonationProvider>
    </BrowserRouter>
  );
};
